import React from 'react';

// Utils
import {
  getDetailCustomFieldValue,
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';
import {
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_SHORT_TEXT,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_YOUTUBE,
} from '../../util/types';

import { DOA_RETURN_POLICY_LISTING_FIELD_KEY } from '../../config/r2rCommercialSeller';
import CustomExtendedDataSection from '../../components/CustomExtendedDataSection/CustomExtendedDataSection.js';
import extendedCss from '../../components/CustomExtendedDataSection/CustomExtendedDataSection.module.css';

import SpecFieldIcon from './SpecFieldIcon';
import listingCss from './ListingPage.module.css';

/** Long prose stays as its own block; short values fold into the specs list. */
const MAX_SPEC_ROW_TEXT_LENGTH = 120;

const isCompactTextSchema = schemaType =>
  schemaType === SCHEMA_TYPE_TEXT ||
  schemaType === SCHEMA_TYPE_SHORT_TEXT ||
  schemaType === SCHEMA_TYPE_LONG;

/**
 * Renders custom listing fields in compact spec-list style:
 * enum/boolean/long/text/multi-enum → Details rows; YouTube stays separate.
 * DOA_policy is excluded when shown in SectionDoaMaybe.
 */
const CustomListingFields = props => {
  const {
    publicData,
    metadata,
    listingFieldConfigs,
    categoryConfiguration,
    intl,
    excludeKeys = [],
    // ListingPageCarousel v2 card chrome; leave off for CoverPhoto / legacy layouts.
    cardChrome = false,
  } = props;

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  const doaText = String(publicData?.[DOA_RETURN_POLICY_LISTING_FIELD_KEY] || '').trim();
  const keysToExclude = new Set([
    ...excludeKeys,
    ...(doaText ? [DOA_RETURN_POLICY_LISTING_FIELD_KEY] : []),
  ]);

  const displayableFieldConfigs = listingFieldConfigs.filter(
    fieldConfig =>
      fieldConfig.showConfig?.displayOnListingPage !== false && !keysToExclude.has(fieldConfig.key)
  );

  const isFieldForSelectedCategories = fieldConfig => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    return isTargetCategory;
  };

  const formatMultiEnumValue = (enumOptions, value) => {
    const selected = Array.isArray(value) ? value : [];
    return selected
      .map(v => enumOptions?.find(o => `${o.option}` === `${v}`)?.label || v)
      .filter(Boolean)
      .join(', ');
  };

  const asSpecRow = (key, label, value) => {
    const text = String(value).trim();
    if (!text || text.length > MAX_SPEC_ROW_TEXT_LENGTH) {
      return null;
    }
    return { key, value: text, label };
  };

  const pickExtendedDataFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, showConfig = {} } = config;
    const listingType = publicData.listingType;
    const isTargetListingType = isFieldForListingType(listingType, config);
    const isTargetCategory = isFieldForCategory(currentCategories, config);

    const { label } = showConfig;
    const publicDataValue = publicData[key];
    const metadataValue = metadata[key];
    const value = publicDataValue != null ? publicDataValue : metadataValue;

    if (
      !isTargetListingType ||
      !isTargetCategory ||
      typeof value === 'undefined' ||
      value === null
    ) {
      return filteredConfigs;
    }

    // Prefer the shared detail formatter for enum/boolean/long/shortText.
    const detailValue = getDetailCustomFieldValue(
      enumOptions,
      value,
      schemaType,
      key,
      label,
      intl,
      'ListingPage'
    );
    if (detailValue) {
      return filteredConfigs.concat(detailValue);
    }

    // Text (and other unhandled short values) → compact label/value row.
    // Important: Console may mark text fields as isDetail, but getDetailCustomFieldValue
    // does not handle schemaType "text", so we must fold them here.
    if (isCompactTextSchema(schemaType)) {
      const row = asSpecRow(key, label, value);
      return row ? filteredConfigs.concat(row) : filteredConfigs;
    }

    if (schemaType === SCHEMA_TYPE_MULTI_ENUM) {
      const joined = formatMultiEnumValue(enumOptions, value);
      if (!joined) {
        return filteredConfigs;
      }
      return filteredConfigs.concat({ key, value: joined, label });
    }

    return filteredConfigs;
  };

  // Keys already rendered as compact spec rows should not also render as SectionText blocks.
  const keysInSpecRows = new Set(
    displayableFieldConfigs.reduce((acc, config) => {
      const rows = pickExtendedDataFields([], config);
      return acc.concat(rows.map(r => r.key));
    }, [])
  );

  const propsForCustomFields = (
    pickCustomFieldProps(
      { publicData, metadata },
      displayableFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || []
  ).filter(field => {
    if (keysInSpecRows.has(field.key)) {
      return false;
    }
    // Keep long text + youtube as separate sections; drop multi-enum already folded.
    return field.schemaType === SCHEMA_TYPE_TEXT || field.schemaType === SCHEMA_TYPE_YOUTUBE;
  });

  const sectionDetailsProps = {
    ...props,
    isFieldForCategory: isFieldForSelectedCategories,
    fieldConfigs: displayableFieldConfigs,
    heading: 'ListingPage.detailsTitle',
    ...(cardChrome
      ? {
          // Replace default section padding so the card gutters are the only horizontal inset.
          rootClassName: listingCss.contentCard,
          detailsListClassName: extendedCss.detailsGrid,
          headerAccessory: (
            <svg
              className={listingCss.sectionHeaderIcon}
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9.2" fill="none" />
              <path d="M12 11v5.5" fill="none" />
              <path d="M12 7.75h.01" fill="none" />
            </svg>
          ),
          renderDetailIcon: fieldKey => <SpecFieldIcon fieldKey={fieldKey} />,
        }
      : {}),
  };

  return (
    <CustomExtendedDataSection
      sectionDetailsProps={sectionDetailsProps}
      propsForCustomFields={propsForCustomFields}
      idPrefix="listingPage"
      pickExtendedDataFields={pickExtendedDataFields}
    />
  );
};

export default CustomListingFields;
